import React, { useEffect, useState } from "react";
import { FaHeart, FaRegHeart, FaShareAlt } from "react-icons/fa";
import { useAuth } from "./AuthContext";
import { useNavigate } from "react-router-dom";
import Spinner from "./Components/Spinner";
import { getVehicleCanonicalUrl, getVehiclePath } from "./Utils/vehicleUrls";

function SavedCars() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cars, setCars] = useState(null);
  const [removingId, setRemovingId] = useState(null);

  useEffect(() => {
    if (!user?.id) {
      navigate("/login");
      return;
    }

    fetch(`${process.env.REACT_APP_API_BASE}/saved/full/${user.id}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    })
      .then((res) => {
        if (!res.ok) throw new Error("Failed to load saved cars");
        return res.json();
      })
      .then((data) => setCars(data))
      .catch((err) => {
        console.error(err);
        setCars([]);
      });
  }, [user, navigate]);

  const handleCarClick = (car) => {
    navigate(getVehiclePath(car));
  };

  const unsaveCar = async (carId) => {
    if (!user?.id || removingId === carId) return;
    setRemovingId(carId);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE}/saved`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: user.id, carId }),
      });

      if (res.ok) {
        setCars((prev) => prev.filter((car) => car.id !== carId));
      }
    } catch (err) {
      console.error("Failed to remove saved car:", err);
    } finally {
      setRemovingId(null);
    }
  };

  if (cars === null) return <Spinner />;

  return (
    <div className="bg-slate-50 px-4 py-6 sm:px-6 lg:px-8">
      <div className="mx-auto w-full max-w-7xl">
        <div className="mb-6 rounded-3xl border border-slate-200 bg-white px-5 py-5 shadow-sm sm:px-6">
          <div className="flex flex-col gap-2">
            <div className="inline-flex w-fit items-center rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-blue-700">
              Meshum dashboard
            </div>
            <h1 className="text-2xl font-semibold tracking-tight text-slate-950 sm:text-3xl">Your saved cars</h1>
            <p className="max-w-2xl text-sm leading-6 text-slate-600">
              Review the vehicles you saved, compare market value, and jump back into the listings you care about.
            </p>
          </div>
        </div>

        {cars.length === 0 ? (
          <EmptySavedCars onBrowse={() => navigate("/search")} />
        ) : (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {cars.map((car) => (
              <SavedCarCard
                key={car.id}
                car={car}
                onClick={handleCarClick}
                onRemove={unsaveCar}
                removing={removingId === car.id}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function SavedCarCard({ car, onClick, onRemove, removing }) {
  const image = car.imageUrl || car.images?.[0] || "https://meshumimages.blob.core.windows.net/websiteimages/defaultCar.jpg";
  const title = [car.year, car.make, car.model, car.trim].filter(Boolean).join(" ");
  const location = [car.dealerCity, car.dealerState].filter(Boolean).join(", ");
  const isSold = car.isActive === false || !!car.soldDate;
  const mestimate = typeof car.mestimate === "number" ? car.mestimate : typeof car.estimate === "number" ? car.estimate : null;
  const delta = mestimate != null && typeof car.price === "number" ? car.price - mestimate : null;

  const handleShare = async (e) => {
    e.stopPropagation();
    const url = getVehicleCanonicalUrl(car, window.location.origin);
    if (navigator.share) {
      try {
        await navigator.share({ title, url });
      } catch {
      }
    } else {
      navigator.clipboard?.writeText(url).catch(() => {});
    }
  };

  return (
    <article className="flex h-full flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
      <button type="button" onClick={() => onClick(car)} className="relative block text-left">
        <img
          src={image}
          alt={`${car.year} ${car.make} ${car.model}`}
          className="h-56 w-full object-cover"
          loading="lazy"
        />
        <span className={`absolute left-3 top-3 rounded-full px-2.5 py-1 text-[11px] font-semibold text-white shadow ${isSold ? "bg-rose-600" : "bg-blue-600"}`}>
          {isSold ? "Sold" : "Saved"}
        </span>
        {isSold && (
          <span className="absolute right-3 top-3 rounded-full bg-white/95 px-2.5 py-1 text-[11px] font-semibold text-rose-700 shadow">
            Saved car
          </span>
        )}
      </button>

      <div className="flex flex-1 flex-col gap-4 p-5">
        <div>
          <div className="text-2xl font-semibold tracking-tight text-slate-950">
            {typeof car.price === "number" ? car.price.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }) : "Price unavailable"}
          </div>
          <h2 className="mt-1 text-base font-semibold leading-snug text-slate-900">{title}</h2>
          <div className="mt-2 space-y-1 text-sm text-slate-600">
            <div>{typeof car.mileage === "number" ? `${car.mileage.toLocaleString()} miles` : "Mileage unavailable"}</div>
            <div>{location || "Dealer/location unavailable"}</div>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Market insight</div>
          {isSold ? (
            <div className="mt-2 text-sm text-slate-600">
              This saved vehicle is sold. Details, photos, and price history remain available.
            </div>
          ) : mestimate != null ? (
            <div className="mt-2 space-y-1">
              <div className="text-sm font-medium text-slate-900">Meshum Estimate {currency(mestimate)}</div>
              {delta != null && (
                <div className={`text-sm font-medium ${delta > 0 ? "text-rose-700" : delta < 0 ? "text-emerald-700" : "text-slate-600"}`}>
                  {delta > 0 ? `${currency(Math.abs(delta))} above estimate` : delta < 0 ? `${currency(Math.abs(delta))} below estimate` : "At estimate"}
                </div>
              )}
            </div>
          ) : (
            <div className="mt-2 text-sm text-slate-600">Estimate unavailable for this saved car.</div>
          )}
        </div>

        <div className="mt-auto flex flex-wrap gap-2">
          <button
            onClick={() => onClick(car)}
            className="inline-flex h-11 flex-1 items-center justify-center rounded-xl bg-blue-600 px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700"
          >
            View details
          </button>
          <button
            onClick={handleShare}
            className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-700 hover:bg-slate-100"
            aria-label="Share car"
            title="Share car"
          >
            <FaShareAlt />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onRemove(car.id);
            }}
            disabled={removing}
            className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-rose-200 bg-rose-50 text-rose-600 hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-60"
            aria-label="Remove saved car"
            title="Remove saved car"
          >
            {removing ? "..." : <FaHeart />}
          </button>
        </div>
      </div>
    </article>
  );
}

function EmptySavedCars({ onBrowse }) {
  return (
    <div className="mx-auto max-w-2xl rounded-3xl border border-dashed border-slate-300 bg-white px-6 py-10 text-center shadow-sm">
      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-blue-50 text-blue-700">
        <FaRegHeart className="text-xl" />
      </div>
      <h2 className="mt-4 text-xl font-semibold text-slate-950">No saved cars yet</h2>
      <p className="mt-2 text-sm leading-6 text-slate-600">
        Save cars from search results or car detail pages to compare them later and keep track of the vehicles you care about.
      </p>
      <button
        onClick={onBrowse}
        className="mt-6 inline-flex h-11 items-center justify-center rounded-xl bg-blue-600 px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700"
      >
        Browse cars
      </button>
    </div>
  );
}

function currency(n) {
  const value = Number(n);
  if (!Number.isFinite(value)) return "—";
  return value.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
}

export default SavedCars;
